type NetworkConnection = {
  effectiveType?: string;
  saveData?: boolean;
  type?: string;
};

type NavigatorWithConnection = Navigator & {
  connection?: NetworkConnection;
};

type RetryFetchOptions = {
  authToken?: string;
  retryDelaysMs?: number[];
  visibilityTimeoutMs?: number;
  waitForVisible?: boolean;
  skipCooldownCheck?: boolean;
  useSharedCooldown?: boolean;
};

const DEFAULT_RETRY_DELAYS_MS = [250, 1000];
const NETWORK_FAILURE_COOLDOWN_MS = 5000;
const TRANSIENT_FETCH_ERROR_RE = /^(Load failed|Failed to fetch|NetworkError when attempting to fetch resource\.?)(?:\s|\(|$)/i;
let networkFailureCooldownUntil = 0;

export const normalizeAuthToken = (value: string): string => value.replace(/[\r\n]+/g, '').trim();

export class TransientNetworkUnavailableError extends Error {
  readonly originalName?: string;
  readonly originalMessage?: string;

  constructor(message: string = 'Recent transport failure', originalError?: unknown) {
    super(message);
    this.name = 'TransientNetworkUnavailableError';

    if (originalError instanceof Error) {
      this.originalName = originalError.name;
      this.originalMessage = originalError.message;
    }
  }
}

export const getNetworkContext = () => {
  const connection = (navigator as NavigatorWithConnection).connection;

  return {
    online: navigator.onLine,
    visibilityState: typeof document === 'undefined' ? 'unknown' : document.visibilityState,
    userAgent: navigator.userAgent,
    connectionType: connection?.type ?? null,
    effectiveType: connection?.effectiveType ?? null,
    saveData: connection?.saveData ?? null,
  };
};

export const isTransientFetchError = (error: unknown): boolean => {
  if (!(error instanceof Error)) return false;
  if (error instanceof TransientNetworkUnavailableError) return true;
  return error.name === 'TypeError' && TRANSIENT_FETCH_ERROR_RE.test(error.message);
};

const isRetriableFetchError = (error: unknown): boolean => {
  if (!(error instanceof Error)) return false;
  if (isTransientFetchError(error)) return true;
  if (error.name === 'TypeError') return true;
  return /load failed|failed to fetch|network/i.test(error.message);
};

const toTransientNetworkError = (error: unknown): TransientNetworkUnavailableError => {
  if (error instanceof TransientNetworkUnavailableError) return error;
  const message = error instanceof Error && error.message ? error.message : 'Recent transport failure';
  return new TransientNetworkUnavailableError(message, error);
};

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });

const waitForDocumentVisible = async (timeoutMs: number): Promise<void> => {
  if (typeof document === 'undefined' || document.visibilityState === 'visible') return;

  await new Promise<void>((resolve) => {
    let settled = false;

    const cleanup = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      resolve();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') cleanup();
    };

    const timer = window.setTimeout(cleanup, timeoutMs);
    document.addEventListener('visibilitychange', onVisibilityChange);
  });
};

export const fetchWithRetry = async (
  input: RequestInfo | URL,
  init: RequestInit = {},
  options: RetryFetchOptions = {}
): Promise<Response> => {
  const {
    authToken,
    retryDelaysMs = DEFAULT_RETRY_DELAYS_MS,
    visibilityTimeoutMs = 1500,
    waitForVisible = true,
    skipCooldownCheck = false,
    useSharedCooldown = true,
  } = options;

  if (useSharedCooldown && !skipCooldownCheck && Date.now() < networkFailureCooldownUntil) {
    throw new TransientNetworkUnavailableError();
  }

  if (waitForVisible) {
    await waitForDocumentVisible(visibilityTimeoutMs);
  }

  const headers = new Headers(init.headers);
  const normalizedAuthToken = authToken ? normalizeAuthToken(authToken) : '';
  if (normalizedAuthToken) {
    headers.set('Authorization', normalizedAuthToken);
  }

  let lastError: unknown;
  for (let attempt = 0; attempt <= retryDelaysMs.length; attempt++) {
    try {
      const response = await fetch(input, {
        ...init,
        headers,
      });
      if (useSharedCooldown) networkFailureCooldownUntil = 0;
      return response;
    } catch (error) {
      lastError = error;
      const isRetriable = isRetriableFetchError(error);
      const isOffline = navigator.onLine === false;

      if (!isRetriable || attempt === retryDelaysMs.length || isOffline) {
        if (isRetriable) {
          if (useSharedCooldown) {
            networkFailureCooldownUntil = Date.now() + NETWORK_FAILURE_COOLDOWN_MS;
          }
          throw toTransientNetworkError(error);
        }
        throw error;
      }

      if (waitForVisible) {
        await waitForDocumentVisible(visibilityTimeoutMs);
      }
      await delay(retryDelaysMs[attempt]);
    }
  }

  throw lastError instanceof Error ? lastError : new Error('Unknown fetch failure');
};
